import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Eye, EyeOff } from 'lucide-react';
import { PublicClientApplication } from '@azure/msal-browser';
import { useAppStore } from '../stores/appStore';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';
import amplinthLogo from '../assets/images/amplinthLogo-new.jpeg';
import { msalConfig, loginRequest } from '../lib/msalConfig';
import { setAuthToken } from '../lib/api';

// Initialize MSAL instance
const msalInstance = new PublicClientApplication(msalConfig);

export default function Login() {
  const navigate = useNavigate();
  const { login, isLoading, isAuthenticated, setCurrentUser, setAuthenticated } = useAppStore((state) => ({
    login: state.login,
    isLoading: state.isLoading,
    isAuthenticated: state.isAuthenticated,
    setCurrentUser: state.setCurrentUser,
    setAuthenticated: state.setAuthenticated,
  }));

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isMicrosoftLoading, setIsMicrosoftLoading] = useState(false);

  // Redirect to dashboard if user is already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard');
    }
  }, [isAuthenticated, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email || !password) {
      toast.error('Please enter both email and password');
      return;
    }

    const result = await login(email, password);

    if (result.ok) {
      toast.success('Welcome back!');
      navigate('/dashboard');
      return;
    }

    switch (result.status) {
      case 403:
        // Account exists but is awaiting administrator approval (Pending role).
        toast.error(
          result.message ||
            'Your account is pending approval. Please contact your administrator to enable access.'
        );
        break;
      case 429:
        // Rate-limited by the backend login guard.
        toast.error(
          result.message || 'Too many failed login attempts. Please try again later.'
        );
        break;
      case 401:
        // Bad credentials. The backend returns the same 401 whether the email is
        // unregistered or the password is wrong (it deliberately doesn't reveal
        // which), so we can't auto-redirect to register — instead we point the
        // user at the "Create one" link with a clear message.
        toast.error(
          "Invalid email or password. If you don't have an account yet, please register."
        );
        break;
      default:
        // No response (network/server error).
        toast.error(
          result.message ||
            'Unable to sign in right now. Please check your connection and try again.'
        );
    }
  };

  const handleMicrosoftLogin = async () => {
    setIsMicrosoftLoading(true);

    try {
      await msalInstance.initialize();
      const loginResponse = await msalInstance.loginPopup(loginRequest);

      console.log('Microsoft login successful:', loginResponse);

      if (loginResponse.account) {
        // Send the Microsoft auth data to backend
        const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/auth/microsoft-login`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            access_token: loginResponse.accessToken,
            id_token: loginResponse.idToken,
            account_name: loginResponse.account.name || loginResponse.account.username,
            account_email: loginResponse.account.username, // Microsoft uses username for email
          }),
        });

        if (response.ok) {
          const data = await response.json();

          // Check if user is pending approval
          if (data.message === 'pending_approval') {
            // Set auth state so they can see the pending page
            setAuthToken(data.token);
            const user = {
              id: data.user.id,
              name: `${data.user.first_name} ${data.user.last_name}`,
              email: data.user.email,
              role: data.user.role,
            };
            setCurrentUser(user);
            setAuthenticated(true);

            await new Promise(resolve => setTimeout(resolve, 100));
            navigate('/dashboard');
            return;
          }

          // Set the auth token in the API client
          setAuthToken(data.token);

          // Transform backend user to frontend User type
          const user = {
            id: data.user.id,
            name: `${data.user.first_name} ${data.user.last_name}`,
            email: data.user.email,
            role: data.user.role,
          };

          // Update app store with user data and authentication state
          setCurrentUser(user);
          setAuthenticated(true);

          toast.success(`Welcome ${loginResponse.account.name}!`);

          // Wait a bit for Zustand persist middleware to save to localStorage
          // This prevents race condition where navigation happens before state is persisted
          await new Promise(resolve => setTimeout(resolve, 100));

          // Always navigate to dashboard - the popup will close automatically after redirect
          navigate('/dashboard');
        } else if (response.status === 403) {
          const errorData = await response.json();
          toast.error(errorData.detail || 'Your account is inactive. Please contact your administrator.');
        } else {
          const errorData = await response.json();
          toast.error(errorData.detail || 'Microsoft SSO backend verification failed');
        }
      }
    } catch (error: any) {
      console.error('Microsoft login error:', error);

      if (error.errorCode === 'user_cancelled') {
        toast.info('Login cancelled');
      } else {
        toast.error('Microsoft login failed. Please try again.');
      }
    } finally {
      setIsMicrosoftLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-secondary-50 via-white to-primary-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo and Title */}
        <div className="text-center mb-8">
          <div className="flex justify-center mb-6">
            <img
              src={amplinthLogo}
              alt="Amplinth Logo"
              className="h-32 w-auto"
            />
          </div>
          <h1 className="text-3xl font-bold text-neutral-900 mb-2">
            Business Transformation Studio
          </h1>
          <p className="text-neutral-600">
            Sign in to access your projects
          </p>
        </div>

        {/* Login Card */}
        <div className="bg-white rounded-lg shadow-xl p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            <Input
              label="Email"
              type="email"
              placeholder="consultant@amplinth.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoFocus
            />

            <div>
              <label
                htmlFor="password"
                className="block text-sm font-medium text-neutral-700 mb-1.5"
              >
                Password
              </label>
              <div className="relative">
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoComplete="current-password"
                  className="w-full px-3 py-2 pr-11 bg-white border border-neutral-300 rounded-lg text-neutral-900 placeholder:text-neutral-400 hover:border-neutral-400 focus:outline-none focus:ring-2 focus:ring-secondary-500 focus:border-transparent transition-all"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-500 hover:text-neutral-700 focus:outline-none"
                  tabIndex={-1}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              className="w-full"
              size="lg"
              loading={isLoading}
              disabled={isLoading}
            >
              Sign In
            </Button>

            {/* Divider */}
            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-neutral-300"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-4 bg-white text-neutral-600">OR</span>
              </div>
            </div>

            {/* Microsoft SSO Button */}
            <button
              type="button"
              onClick={handleMicrosoftLogin}
              disabled={isLoading || isMicrosoftLoading}
              className="w-full flex items-center justify-center gap-3 px-6 py-3 bg-white border border-neutral-300 rounded-lg text-sm font-semibold text-neutral-700 hover:bg-neutral-50 hover:border-neutral-400 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
            >
              {isMicrosoftLoading ? (
                <svg className="w-5 h-5 animate-spin text-neutral-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
              ) : (
                <svg width="20" height="20" viewBox="0 0 21 21" xmlns="http://www.w3.org/2000/svg">
                  <rect x="1" y="1" width="9" height="9" fill="#f25022" />
                  <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
                  <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
                  <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
                </svg>
              )}
              <span>{isMicrosoftLoading ? 'Signing in…' : 'Sign in with Microsoft'}</span>
            </button>
          </form>
        </div>

        {/* Sign-up link */}
        <div className="mt-6 text-center text-sm text-neutral-700">
          Don&apos;t have an account?{' '}
          <Link to="/register" className="text-secondary-600 hover:underline font-semibold">
            Create one
          </Link>
        </div>

        {/* Footer */}
        <div className="mt-8 text-center text-sm text-neutral-600">
          <p>&copy; 2024 Amplinth. All rights reserved.</p>
        </div>
      </div>
    </div>
  );
}
